import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Home, 
  Users, 
  Receipt, 
  Bed, 
  FileText, 
  Settings, 
  LogOut,
  Menu,
  X,
  BadgeIndianRupee,
  UserCircle2
} from 'lucide-react';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'My PGs', href: '/pgs', icon: Home },
  { name: 'Tenants', href: '/tenants', icon: Users },
  { name: 'Collect Rent', href: '/collect-rent', icon: Receipt },
  { name: 'Occupancy', href: '/occupancy', icon: Bed },
  { name: 'Expenses', href: '/expenses', icon: FileText },
  { name: 'Setup', href: '/setup', icon: Settings }
];

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const subscription = user?.subscription;
  const hasActiveSubscription = subscription?.status === 'ACTIVE';
  const noSubscriptionMessage = 'Please contact support you don\'t have any subscription.';
  const daysLeftText = hasActiveSubscription && typeof subscription?.daysLeft === 'number'
    ? `${subscription.daysLeft} day${subscription.daysLeft === 1 ? '' : 's'} left`
    : null;
  const sidebarSubscriptionMessage = hasActiveSubscription
    ? daysLeftText
    : subscription?.status === 'EXPIRED'
      ? 'Your subscription is ended please contact support.'
      : subscription?.message || noSubscriptionMessage;
  const daysLeftClassName = hasActiveSubscription && subscription?.daysLeft > 20
    ? 'text-emerald-800 font-bold'
    : 'text-rose-600 font-bold';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (value) => value ? new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }) : 'Not set';

  const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;

  const subscriptionStatusClassName = subscription?.status === 'ACTIVE'
    ? 'bg-emerald-50 text-emerald-700'
    : subscription?.status === 'CANCELLED'
      ? 'bg-amber-50 text-amber-700'
      : 'bg-rose-50 text-rose-700';

  const UserCard = ({ mobile = false }) => (
    <button
      type="button"
      onClick={() => setProfileOpen(true)}
      className={`w-full rounded-2xl border border-slate-200 bg-slate-50/90 p-3 text-left transition hover:border-slate-300 hover:bg-slate-100 ${mobile ? '' : ''}`}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
          <UserCircle2 className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-900">{user?.name}</p>
          <p className="truncate text-xs text-slate-500">{user?.email}</p>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs font-medium text-slate-500">Subscription</span>
        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${subscription ? subscriptionStatusClassName : 'bg-slate-200 text-slate-700'}`}>
          {subscription?.status || 'NO SUBSCRIPTION'}
        </span>
      </div>
    </button>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-slate-900/50" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div>
                <div>
                  <span className="block text-lg font-bold text-slate-900 font-heading">Solucate</span>
                  <span className={`block text-[11px] ${daysLeftClassName}`}>{sidebarSubscriptionMessage}</span>
                </div>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="p-1">
                <X className="w-6 h-6 text-slate-500" />
              </button>
            </div>
            <nav className="p-4 space-y-2">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
                      isActive
                        ? 'bg-primary text-white'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
            <div className="mt-auto border-t border-slate-200 px-4 py-4">
              <UserCard mobile />
              <Button
                onClick={handleLogout}
                variant="outline"
                className="mt-3 w-full justify-start gap-2 h-10 rounded-lg"
                data-testid="mobile-logout-button"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 bg-white border-r border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <div className="min-w-0">
              <span className="block text-xl font-bold text-slate-900 font-heading">Solucate</span>
              <span className={`block text-xs ${daysLeftClassName}`}>{sidebarSubscriptionMessage}</span>
            </div>
          </div>
          <nav className="flex-1 p-4 space-y-2">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
                    isActive
                      ? 'bg-primary text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          <div className="p-4 border-t border-slate-200">
            <UserCard />
            <Button
              onClick={handleLogout}
              variant="outline"
              className="mt-3 w-full justify-start gap-2 h-10 rounded-lg"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <div className="sticky top-0 z-10 flex items-center gap-4 bg-white border-b border-slate-200 px-4 py-3 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -m-2 text-slate-500 hover:text-slate-700"
            data-testid="mobile-menu-button"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div>
            <div>
              <span className="block text-lg font-bold text-slate-900 font-heading">Solucate</span>
              <span className={`block text-[11px] ${daysLeftClassName}`}>{sidebarSubscriptionMessage}</span>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8">
          {children}
        </main>
      </div>

      {/* Mobile bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 lg:hidden">
        <div className="flex justify-around items-center h-16">
          {navigation.slice(0, 4).map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex flex-col items-center justify-center gap-1 px-3 py-2 transition-colors ${
                  isActive ? 'text-primary' : 'text-slate-500'
                }`}
                data-testid={`mobile-nav-${item.name.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-xs font-medium">{item.name}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Spacing for mobile bottom nav */}
      <div className="h-16 lg:hidden" />

      {profileOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_30px_90px_rgba(15,23,42,0.2)] sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-700">Owner Profile</p>
                <h3 className="mt-2 text-2xl font-bold text-slate-950">Account and subscription</h3>
              </div>
              <button
                type="button"
                onClick={() => setProfileOpen(false)}
                className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-6 rounded-[28px] border border-slate-200 bg-slate-50 p-5">
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <UserCircle2 className="h-8 w-8" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-lg font-bold text-slate-950">{user?.name}</p>
                  <p className="truncate text-sm text-slate-600">{user?.email}</p>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                {subscription ? (
                  <>
                    <div className="flex items-center justify-between rounded-2xl bg-white px-4 py-3">
                      <span className="text-sm font-medium text-slate-500">Plan</span>
                      <span className="text-sm font-bold text-slate-900">{subscription.plan}</span>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl bg-white px-4 py-3">
                        <p className="text-sm font-medium text-slate-500">Start date</p>
                        <p className="mt-1 text-sm font-bold text-slate-900">{formatDate(subscription.startDate)}</p>
                      </div>
                      <div className="rounded-2xl bg-white px-4 py-3">
                        <p className="text-sm font-medium text-slate-500">End date</p>
                        <p className="mt-1 text-sm font-bold text-slate-900">{formatDate(subscription.endDate)}</p>
                      </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl bg-white px-4 py-3">
                        <p className="text-sm font-medium text-slate-500">Amount paid</p>
                        <p className="mt-1 text-sm font-bold text-slate-900">{formatCurrency(subscription.amountPaid)}</p>
                      </div>
                      <div className="rounded-2xl bg-white px-4 py-3">
                        <p className="text-sm font-medium text-slate-500">Status</p>
                        <span className={`mt-2 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${subscriptionStatusClassName}`}>
                          {subscription.status}
                        </span>
                      </div>
                    </div>
                    {typeof subscription.daysLeft === 'number' && subscription.status === 'ACTIVE' && (
                      <div className={`flex items-center gap-2 rounded-2xl px-4 py-3 ${subscription.daysLeft > 20 ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-700'}`}>
                        <BadgeIndianRupee className="h-4 w-4" />
                        <span className="text-sm font-bold">{subscription.daysLeft} day{subscription.daysLeft === 1 ? '' : 's'} left on your plan</span>
                      </div>
                    )}
                    {subscription.message && (
                      <div className="rounded-2xl bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
                        {subscription.message}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="rounded-2xl bg-amber-50 px-4 py-4 text-sm font-medium text-amber-800">
                    {noSubscriptionMessage}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
